import { Link } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Footer from '../../common/Footer';
import Header from '../../common/Header';
import SeoHelmet from '../../common/SeoHelmet';

const MohrCircleTheory = () => {
  return (
    <>
      <SeoHelmet
        title="GeoCalc | Interactive Mohr Circle | Theory"
        description="Theory and equations for plane-stress transformation with Mohr Circle, including principal stresses, maximum shear stress, and effective-stress option."
        url="https://www.geocalc.app/mohr-circle-theory"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="page"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Interactive Mohr Circle | Theory', url: 'https://www.geocalc.app/mohr-circle-theory' },
        ]}
      />
      <Header />
      <div className="theory">
        <div className="theoryContainer">
          <h1>Interactive Mohr Circle | Theory and Assumptions</h1>

          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>Scope</AlertTitle>
            This tool solves 2D plane-stress transformation only. Inputs are σx, σy, τxy, and selected plane angle θ.
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>Sign Convention</AlertTitle>
            Compression-positive normal stress is used. The Mohr-circle τ axis is positive downward; positive τ corresponds to CCW
            shear sense. Angle θ is measured clockwise from x-face, and transformation uses 2θ on the circle.
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>Core Equations</AlertTitle>
            <p className="theoryEquation">
              <math display="block">
                <mrow>
                  <mi>C</mi>
                  <mo>=</mo>
                  <mfrac>
                    <mrow>
                      <msub>
                        <mi>&sigma;</mi>
                        <mi>x</mi>
                      </msub>
                      <mo>+</mo>
                      <msub>
                        <mi>&sigma;</mi>
                        <mi>y</mi>
                      </msub>
                    </mrow>
                    <mn>2</mn>
                  </mfrac>
                </mrow>
              </math>
            </p>
            <p className="theoryEquation">
              <math display="block">
                <mrow>
                  <mi>R</mi>
                  <mo>=</mo>
                  <msqrt>
                    <mrow>
                      <msup>
                        <mrow>
                          <mfrac>
                            <mrow>
                              <msub>
                                <mi>&sigma;</mi>
                                <mi>x</mi>
                              </msub>
                              <mo>-</mo>
                              <msub>
                                <mi>&sigma;</mi>
                                <mi>y</mi>
                              </msub>
                            </mrow>
                            <mn>2</mn>
                          </mfrac>
                        </mrow>
                        <mn>2</mn>
                      </msup>
                      <mo>+</mo>
                      <msubsup>
                        <mi>&tau;</mi>
                        <mrow>
                          <mi>x</mi>
                          <mi>y</mi>
                        </mrow>
                        <mn>2</mn>
                      </msubsup>
                    </mrow>
                  </msqrt>
                </mrow>
              </math>
            </p>
            <p className="theoryEquation">
              <math display="block">
                <mrow>
                  <msub>
                    <mi>&sigma;</mi>
                    <mn>1</mn>
                  </msub>
                  <mo>=</mo>
                  <mi>C</mi>
                  <mo>+</mo>
                  <mi>R</mi>
                  <mo>,</mo>
                  <mspace width="1em" />
                  <msub>
                    <mi>&sigma;</mi>
                    <mn>3</mn>
                  </msub>
                  <mo>=</mo>
                  <mi>C</mi>
                  <mo>-</mo>
                  <mi>R</mi>
                </mrow>
              </math>
            </p>
            <p className="theoryEquation">
              <math display="block">
                <mrow>
                  <msub>
                    <mi>&sigma;</mi>
                    <mi>&theta;</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>C</mi>
                  <mo>+</mo>
                  <mfrac>
                    <mrow>
                      <msub>
                        <mi>&sigma;</mi>
                        <mi>x</mi>
                      </msub>
                      <mo>-</mo>
                      <msub>
                        <mi>&sigma;</mi>
                        <mi>y</mi>
                      </msub>
                    </mrow>
                    <mn>2</mn>
                  </mfrac>
                  <mi>cos</mi>
                  <mo>(</mo>
                  <mn>2</mn>
                  <mi>&theta;</mi>
                  <mo>)</mo>
                  <mo>+</mo>
                  <msub>
                    <mi>&tau;</mi>
                    <mrow>
                      <mi>x</mi>
                      <mi>y</mi>
                    </mrow>
                  </msub>
                  <mi>sin</mi>
                  <mo>(</mo>
                  <mn>2</mn>
                  <mi>&theta;</mi>
                  <mo>)</mo>
                </mrow>
              </math>
            </p>
            <p className="theoryEquation">
              <math display="block">
                <mrow>
                  <msub>
                    <mi>&tau;</mi>
                    <mi>&theta;</mi>
                  </msub>
                  <mo>=</mo>
                  <mo>-</mo>
                  <mfrac>
                    <mrow>
                      <msub>
                        <mi>&sigma;</mi>
                        <mi>x</mi>
                      </msub>
                      <mo>-</mo>
                      <msub>
                        <mi>&sigma;</mi>
                        <mi>y</mi>
                      </msub>
                    </mrow>
                    <mn>2</mn>
                  </mfrac>
                  <mi>sin</mi>
                  <mo>(</mo>
                  <mn>2</mn>
                  <mi>&theta;</mi>
                  <mo>)</mo>
                  <mo>+</mo>
                  <msub>
                    <mi>&tau;</mi>
                    <mrow>
                      <mi>x</mi>
                      <mi>y</mi>
                    </mrow>
                  </msub>
                  <mi>cos</mi>
                  <mo>(</mo>
                  <mn>2</mn>
                  <mi>&theta;</mi>
                  <mo>)</mo>
                </mrow>
              </math>
            </p>
            <p>
              Principal-plane orientation is calculated as θp,CW = 0.5 atan2(2τxy, σx - σy). Maximum-shear orientation is θs,CW =
              θp,CW + 45 deg, and shear magnitude is τmax = R.
            </p>
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>Effective Stress Option</AlertTitle>
            <p>
              When enabled, effective stresses are calculated from σx' = σx - u and σy' = σy - u, where u is pore pressure. Shear
              stress τxy is unchanged.
            </p>
          </Alert>

          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>Limits</AlertTitle>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
              <li>Plane stress only (2D).</li>
              <li>No full 3D stress tensor transformation in this version.</li>
              <li>No failure-envelope fitting or constitutive model calibration in this tool.</li>
            </Box>
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>References</AlertTitle>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
              <li>Timoshenko, S. P., and Goodier, J. N. Theory of Elasticity.</li>
              <li>Das, B. M., and Sobhan, K. Principles of Geotechnical Engineering.</li>
              <li>Budhu, M. Soil Mechanics and Foundations.</li>
              <li>Terzaghi, K., Peck, R. B., and Mesri, G. Soil Mechanics in Engineering Practice.</li>
            </Box>
          </Alert>

          <div className="theoryButtonContainer">
            <Link to="/calculations/mohr-circle">
              <Button variant="outlined" size="small">
                GO TO CALCULATOR
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MohrCircleTheory;
